/**
 * S2's third affordance: **View** — someone else's count, read-only.
 *
 * `docs/spec.md:101`: one responsible manager per session; others can view, but
 * the session locks to its owner for entry, and there is **no admin bypass** —
 * the backend refuses `copeland@cubanbread.com` just as flatly as anyone else
 * (`count/sessions.py` `_assert_owner`). This screen is that rule rendered.
 *
 * **No new draft is ever taken for a session opened here** — `sessions/rules.ts`
 * `mayHoldDraft`: a session that is not mine to enter is not mine to hold a draft
 * for, because that draft could only ever be replayed into a refusal.
 *
 * **But drafts already held have to be dealt with, and this is where that
 * happens** (§ 4.4). D4 found the gap: a session that freezes under a manager —
 * posted or cancelled from another iPad — routes *away* from entry by its status,
 * so a warning living only on the entry screen would never be seen. The held
 * counts, and the explicit discard, therefore belong here, on the screen the
 * frozen session actually lands on. Nothing is flushed and nothing is dropped
 * silently; the manager reads what is held and decides.
 *
 * It also renders the staged lines. A manager standing next to a colleague's
 * half-finished count should be able to see what has been entered — that is what
 * "others can view" is for — and every number here comes from staging as a
 * string, unparsed and unsummed.
 */
import { Link } from "react-router-dom";

import { BlockedFlushNotice } from "../offline/SyncPanel";
import { useDraftSync } from "../offline/useDraftSync";
import { SessionFacts } from "./SessionFacts";
import type { SessionScreenProps } from "./SessionRoute";

export function SessionView({ session, mine, areaId, principal }: SessionScreenProps) {
  // Reads the draft store and never writes to it: `useDraftSync`'s only write
  // paths are `record` (not called here) and the discard the manager asks for.
  const sync = useDraftSync(areaId, session, principal);
  const held = [...sync.drafts.values()].filter((d) => d.sync_state !== "synced");

  return (
    <section>
      <h2>{session.area.display_name} — count (read-only)</h2>

      <BlockedFlushNotice block={sync.block} drafts={held} onDiscard={sync.discard} />

      <div className="card" data-testid="read-only-notice">
        {mine ? (
          <p>
            <strong>Read-only.</strong> This count is yours, but staging has frozen
            it at <code>{session.status}</code>, so there is nothing here to
            change.
          </p>
        ) : (
          <>
            <p>
              <strong>Read-only — this count is not yours.</strong>{" "}
              <code>{session.responsible_manager}</code> is its responsible
              manager.
            </p>
            <p className="muted small">
              A session locks to its responsible manager for entry, and there is
              no override for anyone — including an admin. Two people typing into
              one count is the ambiguity that rule exists to remove. You can read
              it here; nothing about it is saved on this device.
            </p>
          </>
        )}
      </div>

      <div className="card">
        <h3>Session</h3>
        <SessionFacts session={session} mine={mine} />
      </div>

      <div className="card">
        <h3>What has been counted</h3>
        {session.lines.length === 0 ? (
          <p className="muted">
            No items have been entered yet. Blank is not zero: an item that has
            not been counted has no staging line at all, which is how "leave this
            item's inventory unchanged" is guaranteed rather than promised.
          </p>
        ) : (
          <table className="lines" data-testid="staged-lines">
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col">Counted</th>
                <th scope="col">UOM</th>
                <th scope="col">Entered by</th>
              </tr>
            </thead>
            <tbody>
              {session.lines.map((line) => (
                <tr key={line.line_no}>
                  <th scope="row">
                    <code>{line.item_no}</code>
                  </th>
                  {/* The typed string, exactly as staging holds it. This app does
                      no arithmetic on a quantity and never parses one. */}
                  <td className="numeric">{line.counted_qty}</td>
                  <td>{line.unit_of_measure_code ?? "—"}</td>
                  <td className="small">{line.entered_by ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p>
        <Link to={`/areas/${encodeURIComponent(areaId)}`}>
          ← Back to {session.area.display_name}'s counts
        </Link>
      </p>
    </section>
  );
}
